<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onDestroy, onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { devices_config } from 'src/configs/devices';
	import { user_data } from 'src/stores/account/user';
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	import {
		claimRelayPriorityDevHud,
		relayPriorityCloseChrome,
		relayPriorityDevForce,
	} from 'src/stores/features/dev/relay-priority-dev-state';
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import NextScheduleCard from 'src/components/Sidepanel/DeviceViews/NextScheduleCard.svelte';
	import QuickTimerRail from 'src/components/Sidepanel/DeviceViews/Relay/QuickTimerRail.svelte';
	import RelayTimerPanel from 'src/components/Sidepanel/DeviceViews/Relay/RelayTimerPanel.svelte';
	import {
		ControlCard,
		DeviceSwitch,
		PriorityGrid,
		PropertyCard,
		PropertyDock,
		buildActiveSurfaceLightingStyle,
		toast,
	} from '@Shelly/shelly-design-system';
	import { isDevModalAvailable, isNonProductionAppBuild } from 'src/helpers/dev_tools';
	import { cancelDeviceTimer, handleDeviceTurn } from 'src/helpers/handle_device_turn';
	import {
		get_gen_2_control_component_channel_structure,
		isActuator as isActuatorHelper,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import {
		RELAY_DEV_TIMER_REMAINING_SEC,
		buildRelayPmMeterCards,
		buildRelayPriorityLayout,
		collectRelayProtectionErrors,
		isRelayPriorityPmMeter,
		readRelayPmReadings,
		readRelaySwitchState,
		relaySwitchTimerDisplay,
		type RelayQuickTimerOption,
	} from 'src/helpers/relay_priority_layout';
	import { fetchSensorPrioritySpark } from 'src/helpers/sensor_priority_spark';
	import { calculate_timer, clear_device_timer_state } from 'src/helpers/timer_helpers';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import { capFirstChar, wait } from 'src/helpers/utilities';

	export let device: Device;

	let picking = false;
	let isLoading = false;
	let showPinModal = false;
	let panelLeaving = false;
	let panelExecuting = false;
	let pinLocked = false;
	let clockTick = 0;
	let hudTimerRemaining = RELAY_DEV_TIMER_REMAINING_SEC;
	let lastForce = $relayPriorityDevForce;
	let lastSwitchTimerIdentity = '';
	let switchTimer = relaySwitchTimerDisplay({ hasTimer: false });
	let pendingAction: (() => void) | undefined;
	let releaseDevHud: (() => void) | undefined;
	let hudTick: ReturnType<typeof setInterval> | undefined;
	let spark: number[] | undefined;
	let sparkKey = '';

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore as Record<string, unknown> | undefined;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore as Record<string, unknown> | undefined;
	$: isGen2 = is_gen_2(device);
	$: component = isGen2 ? get_gen_2_control_component_channel_structure(device) : '';
	$: channelIndex = Number(device?.channel ?? 0);
	$: hasRights = hasControlRights(device);
	$: isActuator = isActuatorHelper(settings, device);
	$: liveState = readRelaySwitchState(status, component, channelIndex);
	$: uiStateStore = getDeviceState(device?.id);
	$: uiState = $uiStateStore;
	$: liveTimer = (() => {
		clockTick;
		if (!liveState.hasTimer || !settings) {
			return { remaining: undefined as number | undefined, percentage: 100 };
		}
		try {
			const result = calculate_timer(device, status, settings);
			return {
				remaining: typeof result?.[1] === 'number' ? result[1] : undefined,
				percentage: typeof result?.[2] === 'number' ? result[2] : 100,
			};
		} catch {
			return { remaining: undefined as number | undefined, percentage: 100 };
		}
	})();
	$: readings = readRelayPmReadings(status, component, channelIndex);
	$: layout = buildRelayPriorityLayout({
		...liveState,
		timerRemaining: $relayPriorityDevForce === 'timer' ? hudTimerRemaining : liveTimer.remaining,
		online: Boolean(status?.online),
		uiStateType: typeof uiState?.type === 'number' ? uiState.type : undefined,
		force: $relayPriorityDevForce,
		errors: collectRelayProtectionErrors(status, component, channelIndex),
		readings,
	});
	$: switchTimerIdentity = `${layout.timerStarted ?? 0}:${layout.hasTimer}:${$relayPriorityDevForce}`;
	$: if (switchTimerIdentity !== lastSwitchTimerIdentity) {
		lastSwitchTimerIdentity = switchTimerIdentity;
		switchTimer = relaySwitchTimerDisplay({
			hasTimer: layout.hasTimer,
			remaining: layout.timerRemaining,
			percentage: $relayPriorityDevForce === 'timer' ? 100 : liveTimer.percentage,
		});
	}
	$: isButtonDisabled = !status?.online || !hasRights || isActuator;
	$: isControlsDisabled = isButtonDisabled || Boolean(layout.overlay);
	$: if (layout.overlay && picking) picking = false;
	$: hideQuickTimer = Boolean(device?.shared);
	$: usePmMeter =
		isRelayPriorityPmMeter(device?.type) || Boolean(devices_config?.[device?.type]?.isPM);
	$: meterCards = usePmMeter ? buildRelayPmMeterCards(readings) : [];
	$: {
		const nextKey =
			usePmMeter && device?.id ? `${device.id}:${device.channel ?? 0}:consumption` : '';
		if (nextKey !== sparkKey) {
			sparkKey = nextKey;
			spark = undefined;
			if (nextKey) {
				const key = nextKey;
				fetchSensorPrioritySpark(device, 'consumption').then((values) => {
					if (sparkKey === key) spark = values;
				});
			}
		}
	}
	$: switchLightingColor = layout.ison ? '#6FFF44' : '#FF4339';
	$: switchLightingStyle = buildActiveSurfaceLightingStyle({
		active: layout.ison,
		offline: Boolean(layout.overlay),
		hotspotX: '50%',
		hotspotY: layout.ison ? '2.5%' : '97.5%',
		accentColor: switchLightingColor,
		spotColor: switchLightingColor,
		edgeColor: switchLightingColor,
		activeInteriorStrength: 0.31,
		activeEdgeStrength: 0.5,
		inactiveInteriorStrength: 0,
		inactiveEdgeStrength: 0,
		activeInteriorSize: 168,
		activeEdgeSize: 116,
		inactiveInteriorSize: 0,
		inactiveEdgeSize: 0,
		transitionDuration: '280ms',
		transitionDurationLong: '360ms',
	});
	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' &&
		Boolean($devices[device.id]?.pin_lock);

	$: if ($relayPriorityDevForce === 'timer' && lastForce !== 'timer') {
		hudTimerRemaining = RELAY_DEV_TIMER_REMAINING_SEC;
	}
	$: lastForce = $relayPriorityDevForce;

	$: showDevHud = Boolean(isNonProductionAppBuild() && isDevModalAvailable($user_data?.email));

	const syncDevHud = (open: boolean) => {
		if (open) {
			if (!releaseDevHud) releaseDevHud = claimRelayPriorityDevHud();
		} else if (releaseDevHud) {
			releaseDevHud();
			releaseDevHud = undefined;
		}
	};

	$: syncDevHud(showDevHud);

	onMount(() => {
		hudTick = setInterval(() => {
			if (layout.overlay) return;
			clockTick += 1;
			if ($relayPriorityDevForce === 'timer') {
				hudTimerRemaining = Math.max(0, hudTimerRemaining - 1);
			}
		}, 1000);
	});

	onDestroy(() => {
		if (hudTick) clearInterval(hudTick);
		sparkKey = '';
		if (releaseDevHud) {
			releaseDevHud();
			releaseDevHud = undefined;
		}
	});

	const patchStatus = (nextIson: boolean, timerSeconds?: number) => {
		if (!status) return;
		const nextStatus = { ...status };
		if (component) {
			const current = { ...((status[component] as Record<string, unknown> | undefined) ?? {}) };
			if (typeof current.output !== 'undefined' || !('on' in current)) current.output = nextIson;
			else current.on = nextIson;
			if (timerSeconds) {
				current.timer_started_at = Date.now() / 1000;
				current.timer_duration = timerSeconds;
			}
			nextStatus[component] = current;
		} else {
			const relays = Array.isArray(status.relays)
				? [...(status.relays as Record<string, unknown>[])]
				: [];
			const current = { ...(relays[channelIndex] ?? {}) };
			current.ison = nextIson;
			if (timerSeconds) {
				current.has_timer = true;
				current.timer_started = Date.now() / 1000;
				current.timer_duration = timerSeconds;
			}
			relays[channelIndex] = current;
			nextStatus.relays = relays;
		}
		if (!timerSeconds) clear_device_timer_state(nextStatus, device);
		devices_status.update_device_status({
			id: real_id(device.id),
			msg: nextStatus,
		});
	};

	const handleTurn = async (flipbackSeconds?: number) => {
		if (pinLocked) {
			pendingAction = () => handleTurn(flipbackSeconds);
			showPinModal = true;
			return;
		}
		if (isLoading || isControlsDisabled) return;
		isLoading = true;
		try {
			const previousOn = liveState.ison;
			const result = await handleDeviceTurn({ device }, flipbackSeconds);
			if (result.success) {
				patchStatus(!previousOn, flipbackSeconds);
			} else if ($auth_locked_devices.indexOf(real_id(device.id)) === -1) {
				toast.open({
					type: 'danger',
					title: $format('error_global', { default: 'Something went wrong!' }),
				});
			}
		} finally {
			isLoading = false;
			panelLeaving = false;
			panelExecuting = false;
		}
	};

	const handleSelect = (event: CustomEvent<RelayQuickTimerOption>) => {
		picking = false;
		handleTurn(event.detail.seconds);
	};

	const handleCancelTimer = async () => {
		if (pinLocked) {
			pendingAction = handleCancelTimer;
			showPinModal = true;
			return;
		}
		if (isLoading || isControlsDisabled) return;
		panelLeaving = true;
		isLoading = true;
		await wait(220);
		try {
			const result = await cancelDeviceTimer({ device });
			if (result.success) {
				patchStatus(liveState.ison);
			} else if ($auth_locked_devices.indexOf(real_id(device.id)) === -1) {
				toast.open({
					type: 'danger',
					title: $format('error_global', { default: 'Something went wrong!' }),
				});
			}
		} finally {
			isLoading = false;
			panelLeaving = false;
		}
	};

	const handleActNow = async () => {
		if (pinLocked) {
			pendingAction = handleActNow;
			showPinModal = true;
			return;
		}
		if (isLoading || isControlsDisabled) return;
		panelExecuting = true;
		await wait(260);
		await handleTurn();
	};

	const handleClick = (event: Event) => {
		event.stopPropagation();
		event.preventDefault();
		picking = false;
		handleTurn();
	};

	const handleSwipe = (event: CustomEvent<{ direction: 'up' | 'down' }>) => {
		const nextOn = event.detail.direction === 'up';
		if (nextOn === layout.ison) return;
		picking = false;
		handleClick(event);
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		showPinModal = false;
		const action = pendingAction;
		pendingAction = undefined;
		action?.();
	};

	const a11yProps = {
		timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }),
	};
</script>

{#snippet islandInner()}
	{#if !hideQuickTimer}
		<QuickTimerRail
			bind:picking
			armed={layout.hasTimer}
			closeChrome={$relayPriorityCloseChrome}
			disabled={isControlsDisabled}
			idPrefix={`quick-timer-${device.id}`}
			on:select={handleSelect}
		/>
	{/if}
	<div
		class="relay-priority-layout"
		class:is-armed={layout.hasTimer || panelLeaving || panelExecuting}
	>
		<div class="device-switch-host">
			<div class="device-switch-frame" class:is-on={layout.ison} style={switchLightingStyle}>
				<DeviceSwitch
					id="devicebutton-{device.id}"
					value={layout.ison
						? capFirstChar($format('on', { default: 'on' }))
						: capFirstChar($format('off', { default: 'off' }))}
					infoText={capFirstChar($format('label_power', { default: 'Power' }))}
					aria-label={$format('label_turn_device', { default: 'Turn device' })}
					{a11yProps}
					{isLoading}
					hasTimer={layout.hasTimer}
					timer={switchTimer}
					checked={layout.ison}
					enableGestures
					disabled={isControlsDisabled}
					on:click={handleClick}
					on:swipe={handleSwipe}
				/>
			</div>
		</div>
		<div class="relay-timer-slot">
			{#if layout.hasTimer || panelLeaving || panelExecuting}
				<RelayTimerPanel
					idPrefix={`relay-timer-${device.id}`}
					remainingSeconds={layout.timerRemaining ?? 0}
					ison={layout.ison}
					leaving={panelLeaving}
					executing={panelExecuting}
					disabled={isControlsDisabled || isLoading}
					on:cancel={handleCancelTimer}
					on:now={handleActNow}
				/>
			{/if}
		</div>
	</div>
{/snippet}

<DeviceViewStateWrapper
	{device}
	ignoreOfflineState={Boolean(layout.overlay)}
	hideSlotStaticBottom
	hideSlotFloatingBottom={!layout.dockCard}
>
	<div slot="staticTop">
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div class="relay-priority-view" slot="content">
		<ControlCard
			overflow="hidden"
			active={false}
			priority="wide"
			data-property-id="relay"
			disabled={Boolean(layout.overlay)}
			stale={Boolean(layout.overlay)}
			a11yProps={{
				ariaLabel: $format('label_power', { default: 'Power' }),
			}}
		>
			{@render islandInner()}
		</ControlCard>
		<PriorityGrid>
			{#each meterCards as card (card.id)}
				<PropertyCard
					data-property-id={card.id}
					priority={card.priority}
					value={card.value}
					unit={card.unit}
					label={$format(card.label.key, {
						default: card.label.default,
					})}
					icon={card.icon}
					kind={card.kind}
					stale={Boolean(layout.overlay)}
					disabled={Boolean(layout.overlay)}
					chart={card.id === 'apower' ? spark : undefined}
					hideCaption
				/>
			{/each}
			<NextScheduleCard
				{device}
				stale={Boolean(layout.overlay)}
				disabled={Boolean(layout.overlay)}
			/>
		</PriorityGrid>
	</div>

	<svelte:fragment slot="floatingBottom">
		<PropertyDock
			hidden={!layout.dockCard}
			label={$format('label_pinned_device_properties', {
				default: 'Pinned device properties',
			})}
		>
			{#if layout.dockCard}
				<PropertyCard
					data-property-id={layout.dockCard.id}
					priority="primary"
					value={layout.dockCard.numericValue ??
						$format(layout.dockCard.value.key, {
							default: layout.dockCard.value.default,
						})}
					unit={layout.dockCard.unit}
					label={$format(layout.dockCard.label.key, {
						default: layout.dockCard.label.default,
					})}
					icon={layout.dockCard.icon}
					tone={layout.dockCard.tone}
					accentColor={layout.dockCard.accentColor}
					kind={layout.dockCard.kind}
				/>
			{/if}
		</PropertyDock>
	</svelte:fragment>
</DeviceViewStateWrapper>

{#if showPinModal}
	<PinUnlock bind:showModal={showPinModal} on:unlock={handlePin} />
{/if}

<style>
	.relay-priority-view {
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		justify-content: center;
		gap: var(--spacing-s);
		min-width: 0;
		min-height: 0;
	}

	.relay-priority-view :global(.quick-timer-rail) {
		top: var(--spacing-m);
		right: var(--spacing-m);
	}

	.relay-priority-view :global(.quick-timer-rail.is-picking),
	.relay-priority-view :global(.quick-timer-rail.is-collapsing) {
		left: var(--spacing-m);
		right: var(--spacing-m);
	}

	.relay-priority-view :global(.control-card) {
		flex: 0 0 auto;
		width: 100%;
	}

	.relay-priority-layout {
		display: flex;
		align-items: center;
		width: 100%;
		padding-top: calc(var(--spacing-xl) + var(--spacing-m));
		padding-bottom: var(--spacing-l);
	}

	.device-switch-host {
		display: grid;
		flex: 1 1 0;
		min-width: 88px;
		place-items: center;
		transition: flex-grow 380ms cubic-bezier(0.22, 1, 0.36, 1);
	}

	.relay-priority-layout.is-armed .device-switch-host {
		flex-grow: 0;
	}

	.relay-timer-slot {
		display: flex;
		flex: 0 1 0;
		flex-direction: column;
		min-width: 0;
		max-width: 0;
		overflow: hidden;
		transition:
			flex-grow 380ms cubic-bezier(0.22, 1, 0.36, 1),
			max-width 380ms cubic-bezier(0.22, 1, 0.36, 1),
			margin-left 380ms cubic-bezier(0.22, 1, 0.36, 1);
	}

	.relay-priority-layout.is-armed .relay-timer-slot {
		flex-grow: 1;
		max-width: 100%;
		margin-left: var(--spacing-l);
	}

	.device-switch-frame {
		display: grid;
		width: 100%;
		place-items: center;
	}

	.device-switch-frame :global(.device-toggle .slider) {
		isolation: isolate;
		background-color: var(--color-2);
		transition: background-color var(--lighting-transition-duration, 280ms) ease;
	}

	.device-switch-frame.is-on :global(.device-toggle .slider) {
		background-color: color-mix(in srgb, rgb(var(--lighting-accent-rgb)) 8%, #1e1e1e);
	}

	.device-switch-frame :global(.device-toggle .slider::before) {
		position: absolute;
		z-index: 3;
		inset: -1px;
		padding: 1px;
		border-radius: inherit;
		background: var(--lighting-edge-background);
		content: '';
		filter: drop-shadow(0 0 5px rgb(var(--lighting-accent-rgb) / 24%));
		opacity: var(--lighting-edge-opacity);
		-webkit-mask:
			linear-gradient(#000 0 0) content-box,
			linear-gradient(#000 0 0);
		-webkit-mask-composite: xor;
		mask:
			linear-gradient(#000 0 0) content-box,
			linear-gradient(#000 0 0);
		mask-composite: exclude;
		pointer-events: none;
		transition: opacity var(--lighting-transition-duration, 280ms) ease;
	}

	.device-switch-frame :global(.device-toggle .slider::after) {
		position: absolute;
		z-index: 0;
		top: var(--lighting-spot-y);
		left: var(--lighting-spot-x);
		width: var(--lighting-spot-size);
		height: var(--lighting-spot-size);
		border-radius: 50%;
		background: var(--lighting-spot-background);
		content: '';
		opacity: var(--lighting-spot-opacity);
		transform: translate(-50%, -50%);
		transition:
			top var(--lighting-transition-duration-long, 360ms) cubic-bezier(0.22, 1, 0.36, 1),
			opacity var(--lighting-transition-duration, 280ms) ease,
			width var(--lighting-transition-duration-long, 360ms) ease,
			height var(--lighting-transition-duration-long, 360ms) ease;
		pointer-events: none;
	}
</style>
